import { defineStore, storeToRefs } from "pinia";
import { computed, ref, Ref } from "vue";
import { useI18n } from "vue-i18n";
import { Icon } from "../assets/icons/Icon";
import { Module } from "../common/enum.module";
import { ComponentType } from "../component/enum.componentType";
import { useComponent } from "../component/service.component";
import { useUser } from "../composition-functions/user";
import { UserType } from "../composition-functions/user/UserType";
import { config } from "../config/config";
import { MandantAttributeType } from "../mandant/enum.mandantAttributeType";
import { useMandant } from "../mandant/service.mandant";
import { MenuItemType } from "./enum.menuItemType";
import { DashboardSection } from "./model.dashboard";

export const useMandantDashboard = defineStore("mandantDashboardStore", () => {
  const { t } = useI18n();
  const userStore = useUser();
  const { userType, canAccessSensitiveMandantData } = storeToRefs(userStore);

  const mandantStore = useMandant();
  const { currentMandant } = storeToRefs(mandantStore);
  const { doesCurrentMandantHaveActiveAttribute, doesCurrentMandantHaveActiveModule } = mandantStore;

  const activatableComponents: Ref<null | ComponentType[]> = ref(null);

  async function refreshData(mandantId: number) {
    const { getActivatableComponents } = useComponent();
    const getActivatableComponentsResult = await getActivatableComponents(mandantId);
    activatableComponents.value = getActivatableComponentsResult;
  }

  function shouldShowDashboardItem(requiredAttributes: MandantAttributeType[]) {
    if (userType.value === UserType.SUPER_ADMIN || userType.value === UserType.ADMIN) {
      return true;
    }
    return requiredAttributes.every((attr) => doesCurrentMandantHaveActiveAttribute(attr));
  }

  const mandantDashboardConfiguration = computed((): DashboardSection[] => {
    if (!currentMandant.value) return [];

    return [
      {
        title: t("section.portals"),
        isVisible: [UserType.ADMIN, UserType.SUPER_ADMIN].includes(userType.value),
        items: [
          {
            type: MenuItemType.ROUTE,
            icon: Icon.SETTINGS_DOUBLE,
            title: t("pages.mandantDetail.title"),
            subtitle: t("pages.mandantDetail.subtitle"),
            to: config.eassistantProductionUrl + "/MandantDetail/Edit/" + currentMandant.value.id,
            isVisible: true,
            isDisabled: false,
          },
          {
            type: MenuItemType.ROUTE,
            icon: Icon.LINK,
            title: t("pages.mandantLink.links.title"),
            subtitle: t("pages.mandantLink.links.subtitle"),
            to: config.eassistantProductionUrl + "/MandantLink/Links/" + currentMandant.value.id,
            isVisible: true,
            isDisabled: false,
          },
          {
            type: MenuItemType.ROUTE,
            icon: Icon.CALENDAR,
            title: t("pages.mandantLink.overview.title"),
            subtitle: t("pages.mandantLink.overview.subtitle"),
            to: config.eassistantProductionUrl + "/MandantLink/MandantLinkOverview/" + currentMandant.value.id,
            isVisible: true,
            isDisabled: false,
          },
          {
            type: MenuItemType.ROUTE,
            icon: Icon.ROOM_SERVICE,
            title: t("pages.portalAdditional.title"),
            subtitle: t("pages.portalAdditional.subtitle"),
            to: config.eassistantProductionUrl + "/PortalAdditional/Mandant/" + currentMandant.value.id,
            isVisible: true,
            isDisabled: false,
          },
        ],
      },
      {
        title: t("section.administration"),
        isVisible: true,
        items: [
          {
            type: MenuItemType.ROUTE,
            icon: Icon.CALENDAR_ROOMS,
            title: t("pages.reservation.roomPlan.title"),
            subtitle: t("pages.reservation.roomPlan.subtitle"),
            to: config.eassistantProductionUrl + "/Reservation/RoomPlan/" + currentMandant.value.id,
            isVisible:
              shouldShowDashboardItem([MandantAttributeType.ENABLE_ROOM_PLAN]) ||
              shouldShowDashboardItem([MandantAttributeType.ENABLE_ROOM_PLAN_VIEW]),
            isDisabled: false,
          },
          {
            type: MenuItemType.ROUTE,
            icon: Icon.NOTES_BOOK,
            title: t("pages.reservation.title"),
            subtitle: t("pages.reservation.subtitle"),
            to: config.eassistantProductionUrl + "/Reservation/Index/" + currentMandant.value.id,
            isVisible: true,
            isDisabled: !canAccessSensitiveMandantData.value,
          },
          {
            type: MenuItemType.ROUTE,
            icon: Icon.POSTBOX,
            title: t("pages.reservation.requests.title"),
            subtitle: t("pages.reservation.requests.subtitle"),
            to: config.eassistantProductionUrl + "/Reservation/Requests/" + currentMandant.value.id,
            isVisible: true,
            isDisabled: !canAccessSensitiveMandantData.value,
          },
          {
            type: MenuItemType.ROUTE,
            icon: Icon.COMPANY,
            title: t("pages.company.list.title"),
            subtitle: t("pages.company.list.subtitle"),
            to: { name: "companiesList", params: { mandantId: currentMandant.value.id } },
            isVisible: shouldShowDashboardItem([MandantAttributeType.ENABLE_GUEST_EDIT]),
            isDisabled: !canAccessSensitiveMandantData.value,
          },
          {
            type: MenuItemType.ROUTE,
            icon: Icon.TEAM,
            title: t("pages.guests.title"),
            subtitle: t("pages.guests.subtitle"),
            to: { name: "guestsList", params: { mandantId: currentMandant.value.id } },
            isVisible: shouldShowDashboardItem([MandantAttributeType.ENABLE_GUEST_EDIT]),
            isDisabled: !canAccessSensitiveMandantData.value,
          },
          {
            type: MenuItemType.ROUTE,
            icon: Icon.EMAIL_CLOSED,
            title: t("pages.mail.sent.title"),
            subtitle: t("pages.mail.sent.subtitle"),
            to: config.eassistantProductionUrl + "/Mail/Sent/" + currentMandant.value.id,
            isVisible: true,
            isDisabled: !canAccessSensitiveMandantData.value,
          },
          {
            type: MenuItemType.ROUTE,
            icon: Icon.ALARM_CLOCK,
            title: t("pages.roomGap.title"),
            subtitle: t("pages.roomGap.subtitle"),
            to: config.eassistantProductionUrl + "/Gap/Index/" + currentMandant.value.id,
            isVisible: shouldShowDashboardItem([MandantAttributeType.ENABLE_LAST_MINUTE]),
            isDisabled: !canAccessSensitiveMandantData.value,
          },
          {
            type: MenuItemType.ROUTE,
            icon: Icon.POLICEMAN,
            title: t("pages.declaration.police.title"),
            subtitle: t("pages.declaration.police.subtitle"),
            to: config.eassistantProductionUrl + "/Declaration/Police/" + currentMandant.value.id,
            isVisible: shouldShowDashboardItem([MandantAttributeType.ENABLE_ROOM_PLAN]),
            isDisabled: !canAccessSensitiveMandantData.value,
          },
          {
            type: MenuItemType.ROUTE,
            icon: Icon.REPORT,
            title: t("pages.declaration.astat.title"),
            subtitle: t("pages.declaration.astat.subtitle"),
            to: config.eassistantProductionUrl + "/Declaration/Astat/" + currentMandant.value.id,
            isVisible: shouldShowDashboardItem([MandantAttributeType.ENABLE_ROOM_PLAN]),
            isDisabled: !canAccessSensitiveMandantData.value,
          },
          {
            type: MenuItemType.ROUTE,
            icon: Icon.TRANSACTION,
            title: t("pages.mandantPayment.transactions.title"),
            subtitle: t("pages.mandantPayment.transactions.subtitle"),
            to: config.eassistantProductionUrl + "/MandantPayment/Transactions/" + currentMandant.value.id,
            isVisible: shouldShowDashboardItem([MandantAttributeType.ENABLE_TRANSACTIONS]),
            isDisabled: !canAccessSensitiveMandantData.value,
          },
        ],
      },
      {
        title: t("modules.dataAnalysis"),
        isVisible: true,
        items: [
          {
            type: MenuItemType.ROUTE,
            icon: Icon.BARS_CHART,
            title: t("pages.mandantStat.roomPlanStats.title"),
            subtitle: t("pages.mandantStat.roomPlanStats.subtitle"),
            to: config.eassistantProductionUrl + "/MandantStat/RoomPlanStats/" + currentMandant.value.id,
            isVisible: shouldShowDashboardItem([MandantAttributeType.ENABLE_ROOM_PLAN]),
            isDisabled: !canAccessSensitiveMandantData.value,
          },
          {
            type: MenuItemType.ROUTE,
            icon: Icon.OFFICE_FOLDERS,
            title: t("pages.lists.title"),
            subtitle: t("pages.lists.subtitle"),
            to: config.eassistantProductionUrl + "/Lists/Mandant/" + currentMandant.value.id,
            isVisible: true,
            isDisabled: !canAccessSensitiveMandantData.value,
          },
        ],
      },
      {
        title: t("modules.common"),
        isVisible: true,
        items: [
          {
            type: MenuItemType.ROUTE,
            icon: Icon.BOSS,
            title: t("pages.mandant.edit.title"),
            subtitle: t("pages.mandant.edit.subtitle"),
            to: { name: "mandantEdit", params: { mandantId: currentMandant.value.id } },
            isVisible: [UserType.ADMIN, UserType.SUPER_ADMIN].includes(userType.value),
            isDisabled: false,
          },
          {
            type: MenuItemType.ROUTE,
            icon: Icon.COLLABORATION,
            title: t("pages.user.list.title"),
            subtitle: t("pages.user.list.subtitle"),
            to: { name: "userList", query: { mandants: currentMandant.value.id } },
            isVisible: [UserType.ADMIN, UserType.SUPER_ADMIN].includes(userType.value),
            isDisabled: false,
          },
          {
            type: MenuItemType.ROUTE,
            icon: Icon.BROWSER,
            title: t("pages.module.list.title"),
            subtitle: t("pages.module.list.subtitle"),
            to: { name: "modulesEdit", params: { mandantId: currentMandant.value.id } },
            isVisible: true,
            isDisabled: false,
          },
          {
            type: MenuItemType.ROUTE,
            icon: Icon.THEMES,
            title: t("pages.widget.list.title"),
            subtitle: t("pages.widget.list.subtitle"),
            to: { name: "widgetsList", params: { mandantId: currentMandant.value.id } },
            isVisible: true,
            isDisabled: false,
          },
          {
            type: MenuItemType.ROUTE,
            icon: Icon.THEMES,
            title: t("pages.mandantForm.title"),
            subtitle: t("pages.mandantForm.subtitle"),
            to: config.eassistantProductionUrl + "/MandantForm/Index/" + currentMandant.value.id,
            isVisible: [UserType.ADMIN, UserType.SUPER_ADMIN].includes(userType.value),
            isDisabled: false,
          },
          {
            type: MenuItemType.ROUTE,
            icon: Icon.GALLERY,
            title: t("pages.fileManagement.title"),
            subtitle: t("pages.fileManagement.subtitle"),
            to: { name: "itemsDashboard", params: { mandantId: currentMandant.value.id } },
            isVisible: true,
            isDisabled: false,
          },
          {
            type: MenuItemType.ROUTE,
            icon: Icon.NETWORKING,
            title: t("pages.externalSystem.list.title"),
            subtitle: t("pages.externalSystem.list.subtitle"),
            to: { name: "externalSystemsList", params: { mandantId: currentMandant.value.id } },
            isVisible: true,
            isDisabled: false,
          },
          {
            type: MenuItemType.ROUTE,
            icon: Icon.DEBIT_CARD,
            title: t("pages.payment.paymentSystem.list.title"),
            subtitle: t("pages.payment.paymentSystem.list.subtitle"),
            to: { name: "paymentSystemsList", params: { mandantId: currentMandant.value.id } },
            isVisible: true,
            isDisabled: false,
          },
          {
            type: MenuItemType.ROUTE,
            icon: Icon.DEBIT_CARD,
            title: t("pages.mandantPayment.title"),
            subtitle: t("pages.mandantPayment.subtitle"),
            to: config.eassistantProductionUrl + "/MandantPayment/Edit/" + currentMandant.value.id,
            isVisible: [UserType.ADMIN, UserType.SUPER_ADMIN].includes(userType.value),
            isDisabled: !canAccessSensitiveMandantData.value,
          },
          {
            type: MenuItemType.ROUTE,
            icon: Icon.OFFICE_FOLDERS,
            title: t("pages.component.list.title"),
            subtitle: t("pages.component.list.subtitle"),
            to: { name: "componentsList", params: { mandantId: currentMandant.value.id } },
            isVisible: true,
            isDisabled: false,
          },
          {
            type: MenuItemType.ROUTE,
            icon: Icon.CHECKLIST,
            title: t("pages.checklist.title"),
            subtitle: t("pages.checklist.subtitle"),
            to: { name: "checklist", params: { mandantId: currentMandant.value.id } },
            isVisible: true,
            isDisabled: false,
          },
        ],
      },
      {
        title: t("section.settings"),
        isVisible: true,
        items: [
          {
            type: MenuItemType.ROUTE,
            icon: Icon.HOTEL_SETTINGS,
            title: t("pages.mandantSettings.title"),
            subtitle: t("pages.mandantSettings.subtitle"),
            to: config.eassistantProductionUrl + "/MandantSettings/Edit/" + currentMandant.value.id,
            isVisible: true,
            isDisabled: false,
          },
          {
            type: MenuItemType.ROUTE,
            icon: Icon.TWIN_BEDS,
            title: t("pages.roomType.title"),
            subtitle: t("pages.roomType.subtitle"),
            to: config.eassistantProductionUrl + "/RoomType/Index/" + currentMandant.value.id,
            isVisible:
              shouldShowDashboardItem([MandantAttributeType.ENABLE_ROOM_TYPE_EDIT]) ||
              doesCurrentMandantHaveActiveModule(Module.BOOKED),
            isDisabled: false,
          },
          {
            type: MenuItemType.ROUTE,
            icon: Icon.SINGLE_BED,
            title: t("pages.room.title"),
            subtitle: t("pages.room.subtitle"),
            to: config.eassistantProductionUrl + "/Room/Index/" + currentMandant.value.id,
            isVisible:
              shouldShowDashboardItem([MandantAttributeType.ENABLE_ROOM_TYPE_EDIT]) ||
              doesCurrentMandantHaveActiveModule(Module.BOOKED),
            isDisabled: false,
          },
          {
            type: MenuItemType.ROUTE,
            icon: Icon.MONEY,
            title: t("pages.ratePlan.title"),
            subtitle: t("pages.ratePlan.subtitle"),
            to: config.eassistantProductionUrl + "/RatePlan/Index/" + currentMandant.value.id,
            isVisible:
              shouldShowDashboardItem([MandantAttributeType.ENABLE_RATE_EDIT]) ||
              doesCurrentMandantHaveActiveModule(Module.BOOKED),
            isDisabled: false,
          },
          {
            type: MenuItemType.ROUTE,
            icon: Icon.ROOM_SERVICE_TRAY,
            title: t("pages.extra.title"),
            subtitle: t("pages.extra.subtitle"),
            to: config.eassistantProductionUrl + "/AdditionalService/Index/" + currentMandant.value.id,
            isVisible:
              shouldShowDashboardItem([MandantAttributeType.ENABLE_EXTRAS_EDIT]) ||
              doesCurrentMandantHaveActiveModule(Module.BOOKED),
            isDisabled: false,
          },
          {
            type: MenuItemType.ROUTE,
            icon: Icon.PRICE_LIST,
            title: t("pages.priceList.title"),
            subtitle: t("pages.priceList.subtitle"),
            to: config.eassistantProductionUrl + "/PriceList/Edit/" + currentMandant.value.id,
            isVisible: shouldShowDashboardItem([MandantAttributeType.ENABLE_PRICE_LIST]),
            isDisabled: false,
          },
          {
            type: MenuItemType.ROUTE,
            icon: Icon.CALENDAR_DAY,
            title: t("pages.availabilities.title"),
            subtitle: t("pages.availabilities.subtitle"),
            to: config.eassistantProductionUrl + "/Availability/Index/" + currentMandant.value.id,
            isVisible:
              shouldShowDashboardItem([MandantAttributeType.ENABLE_AVAILABILITY_EDIT]) ||
              doesCurrentMandantHaveActiveModule(Module.BOOKED),
            isDisabled: false,
          },
          {
            type: MenuItemType.ROUTE,
            icon: Icon.GALLERY,
            title: t("pages.mandantImage.title"),
            subtitle: t("pages.mandantImage.subtitle"),
            to: config.eassistantProductionUrl + "/MandantImage/Edit/" + currentMandant.value.id,
            isVisible: shouldShowDashboardItem([MandantAttributeType.ENABLE_DETAIL_PAGE_EDIT]),
            isDisabled: false,
          },
          {
            type: MenuItemType.ROUTE,
            icon: Icon.MAP_PIN,
            title: t("pages.mandantLocation.title"),
            subtitle: t("pages.mandantLocation.subtitle"),
            to: config.eassistantProductionUrl + "/MandantLocation/Edit/" + currentMandant.value.id,
            isVisible: shouldShowDashboardItem([MandantAttributeType.ENABLE_DETAIL_PAGE_EDIT]),
            isDisabled: false,
          },
          {
            type: MenuItemType.ROUTE,
            icon: Icon.OPEN,
            title: t("pages.openingPeriod.title"),
            subtitle: t("pages.openingPeriod.subtitle"),
            to: config.eassistantProductionUrl + "/OpeningPeriod/Index/" + currentMandant.value.id,
            isVisible: shouldShowDashboardItem([MandantAttributeType.ENABLE_ROOM_PLAN]),
            isDisabled: false,
          },
          {
            type: MenuItemType.ROUTE,
            icon: Icon.NETWORKING,
            title: t("pages.interface.title"),
            subtitle: t("pages.interface.subtitle"),
            to: config.eassistantProductionUrl + "/Interface/Index/" + currentMandant.value.id,
            isVisible: true,
            isDisabled: false,
          },
          {
            type: MenuItemType.ROUTE,
            icon: Icon.VAT,
            title: t("pages.vatSetting.list.title"),
            subtitle: t("pages.vatSetting.list.subtitle"),
            to: { name: "vatSettingList", params: { mandantId: currentMandant.value.id } },
            isVisible: shouldShowDashboardItem([MandantAttributeType.ENABLE_ROOM_PLAN]),
            isDisabled: false,
          },
          {
            type: MenuItemType.ROUTE,
            icon: Icon.CASH_REGISTER,
            title: t("pages.cashRegister.title"),
            subtitle: t("pages.cashRegister.subtitle"),
            isVisible: shouldShowDashboardItem([MandantAttributeType.ENABLE_ROOM_PLAN]),
            to: { name: "cashRegisterList", params: { mandantId: currentMandant.value.id } },
            isDisabled: false,
          },
          {
            type: MenuItemType.ROUTE,
            icon: Icon.DIARY,
            title: t("pages.mandantText.title"),
            subtitle: t("pages.mandantText.subtitle"),
            to: { name: "mandantTexts", params: { mandantId: currentMandant.value.id } },
            isVisible: true,
            isDisabled: false,
          },
        ],
      },
      {
        title: t("section.tourismPortals"),
        isVisible: shouldShowDashboardItem([MandantAttributeType.ENABLE_DETAIL_PAGE_EDIT]),
        items: [
          {
            type: MenuItemType.ROUTE,
            icon: Icon.OFFER,
            title: t("pages.spPackage.title"),
            subtitle: t("pages.spPackage.subtitle"),
            to: config.eassistantProductionUrl + "/SpPackage/Index/" + currentMandant.value.id,
            isVisible: true,
            isDisabled: false,
          },
          {
            type: MenuItemType.ROUTE,
            icon: Icon.WAREHOUSE,
            title: t("pages.mandantEquipment.title"),
            subtitle: t("pages.mandantEquipment.subtitle"),
            to: config.eassistantProductionUrl + "/MandantEquipment/Edit/" + currentMandant.value.id,
            isVisible: true,
            isDisabled: false,
          },
          {
            type: MenuItemType.ROUTE,
            icon: Icon.SHEET_WRITING,
            title: t("pages.mandantText.detailPage.title"),
            subtitle: t("pages.mandantText.detailPage.subtitle"),
            to: config.eassistantProductionUrl + "/MandantText/DetailPage/" + currentMandant.value.id,
            isVisible: true,
            isDisabled: false,
          },
          {
            type: MenuItemType.ROUTE,
            icon: Icon.BARS_CHART,
            title: t("pages.mandantStat.detail.title"),
            subtitle: t("pages.mandantStat.detail.subtitle"),
            to: config.eassistantProductionUrl + "/MandantStat/Detail/" + currentMandant.value.id,
            isVisible: true,
            isDisabled: false,
          },
        ],
      },
      {
        title: t("modules.connect"),
        isVisible: doesCurrentMandantHaveActiveModule(Module.CONNECT),
        items: [
          {
            type: MenuItemType.ROUTE,
            icon: Icon.SHEET_WRITING,
            title: t("pages.greeting.list.title"),
            subtitle: t("pages.greeting.list.subtitle"),
            to: { name: "greetingsList", params: { mandantId: currentMandant.value.id } },
            isVisible: true,
            isDisabled: false,
          },
          {
            type: MenuItemType.ROUTE,
            icon: Icon.EMAIL,
            title: t("pages.mailLayout.edit.title"),
            subtitle: "",
            to: { name: "baseLayout", params: { mandantId: currentMandant.value.id } },
            isVisible: true,
            isDisabled: false,
          },
          {
            type: MenuItemType.ROUTE,
            icon: Icon.EMAIL_CLOSED,
            title: t("pages.mailTemplateConfig.dashboard.title"),
            subtitle: "",
            to: { name: "mailContentDashboard", params: { mandantId: currentMandant.value.id } },
            isVisible: true,
            isDisabled: false,
          },
          {
            type: MenuItemType.ROUTE,
            icon: Icon.COLLABORATION,
            title: t("pages.mandantSalutation.edit.title"),
            subtitle: "",
            to: { name: "mandantSalutations", params: { mandantId: currentMandant.value.id } },
            isVisible: true,
            isDisabled: false,
          },
        ],
      },
      {
        title: t("modules.crmSync"),
        isVisible: activatableComponents.value?.includes(ComponentType.CRM_SYNC) ?? false,
        items: [
          {
            type: MenuItemType.ROUTE,
            icon: Icon.SETTINGS_DOUBLE,
            title: t("pages.crmSync.edit.title"),
            subtitle: t("pages.crmSync.edit.subtitle"),
            to: { name: "componentEdit", params: { mandantId: currentMandant.value.id, componentIdOrComponentType: "crmSync" } },
            isVisible: true,
            isDisabled: false,
          },
        ],
      },
      {
        title: t("modules.newsletterSync"),
        isVisible: activatableComponents.value?.includes(ComponentType.NEWSLETTER_SYNC) ?? false,
        items: [
          {
            type: MenuItemType.ROUTE,
            icon: Icon.SETTINGS_DOUBLE,
            title: t("pages.newsletterSync.edit.title"),
            subtitle: t("pages.newsletterSync.edit.subtitle"),
            to: {
              name: "componentEdit",
              params: { mandantId: currentMandant.value.id, componentIdOrComponentType: "newsletterSync" },
            },
            isVisible: true,
            isDisabled: false,
          },
        ],
      },
      {
        title: t("modules.discountCode"),
        isVisible: activatableComponents.value?.includes(ComponentType.DISCOUNT_CODE) ?? false,
        items: [
          {
            type: MenuItemType.ROUTE,
            icon: Icon.SETTINGS_DOUBLE,
            title: t("pages.discountCode.edit.title"),
            subtitle: t("pages.discountCode.edit.subtitle"),
            to: {
              name: "componentEdit",
              params: { mandantId: currentMandant.value.id, componentIdOrComponentType: "discountCode" },
            },
            isVisible: true,
            isDisabled: false,
          },
          {
            type: MenuItemType.ROUTE,
            icon: Icon.OFFER,
            title: t("pages.discountCode.list.title"),
            subtitle: t("pages.discountCode.list.subtitle"),
            to: { name: "discountCodesList", params: { mandantId: currentMandant.value.id } },
            isVisible: true,
            isDisabled: false,
          },
        ],
      },
      {
        title: t("modules.voucher"),
        isVisible: activatableComponents.value?.includes(ComponentType.VOUCHER) ?? false,
        items: [
          {
            type: MenuItemType.ROUTE,
            icon: Icon.SETTINGS_DOUBLE,
            title: t("pages.voucher.edit.title"),
            subtitle: t("pages.voucher.edit.subtitle"),
            to: { name: "componentEdit", params: { componentIdOrComponentType: "voucher" } },
            isVisible: true,
            isDisabled: false,
          },
          {
            type: MenuItemType.ROUTE,
            icon: Icon.OFFER,
            title: t("pages.voucher.voucher.list.title"),
            subtitle: t("pages.voucher.voucher.list.subtitle"),
            to: { name: "vouchersList" },
            isVisible: true,
            isDisabled: false,
          },
          {
            type: MenuItemType.ROUTE,
            icon: Icon.ROOM_SERVICE,
            title: t("pages.voucher.voucherElement.list.title"),
            subtitle: t("pages.voucher.voucherElement.list.subtitle"),
            to: { name: "voucherElementsList" },
            isVisible: true,
            isDisabled: false,
          },
          {
            type: MenuItemType.ROUTE,
            icon: Icon.OFFICE_FOLDERS,
            title: t("pages.voucher.voucherCategory.list.title"),
            subtitle: t("pages.voucher.voucherCategory.list.subtitle"),
            to: { name: "voucherCategoriesList" },
            isVisible: true,
            isDisabled: false,
          },
          {
            type: MenuItemType.ROUTE,
            icon: Icon.MONEY,
            title: t("pages.voucher.orderedVoucher.list.title"),
            subtitle: t("pages.voucher.orderedVoucher.list.subtitle"),
            to: { name: "orderedVouchersList" },
            isVisible: true,
            isDisabled: !canAccessSensitiveMandantData.value,
          },
        ],
      },
    ];
  });

  async function onMountedHandler() {
    if (currentMandant.value) {
      await refreshData(currentMandant.value.id);
    }
  }

  return {
    activatableComponents,
    mandantDashboardConfiguration,
    refreshData,
    onMountedHandler,
  };
});
